import { useMemo } from 'react';
import { Chip, Icon, IconButton, Stack, Text, useRovingTabindex, tokens } from '@/design';
import { selectionBus } from '@/state/selectionBus';
import type { ThreeDObjectNode } from './model';

interface ThreeDObjectTreeProps {
  nodes: ThreeDObjectNode[];
  selectedLayerId?: string | null;
  onSelectNode(node: ThreeDObjectNode): void;
  onToggleVisibility(node: ThreeDObjectNode): void;
}

function disciplineLabel(node: ThreeDObjectNode): string {
  if (node.discipline === 'electrical') return 'Electrical';
  if (node.discipline === 'mechanical') return 'Mechanical';
  if (node.discipline === 'structural') return 'Structural';
  return 'General';
}

export function ThreeDObjectTree({
  nodes,
  selectedLayerId,
  onSelectNode,
  onToggleVisibility,
}: ThreeDObjectTreeProps) {
  const sortedNodes = useMemo(
    () =>
      [...nodes].sort((left, right) =>
        `${disciplineLabel(left)}:${left.label}`.localeCompare(
          `${disciplineLabel(right)}:${right.label}`,
        ),
      ),
    [nodes],
  );
  const roving = useRovingTabindex({
    count: sortedNodes.length,
    orientation: 'vertical',
    onActivate: (index) => {
      const node = sortedNodes[index];
      if (!node) return;
      onSelectNode(node);
      selectionBus.publish({
        source: 'host',
        ts: Date.now(),
        features: [{ layerId: node.layerId, featureId: node.id }],
      });
    },
  });

  return (
    <section
      role="tree"
      aria-label="3D object tree"
      style={{
        borderTop: tokens.border.thin,
        padding: tokens.space[2],
        display: 'grid',
        gap: tokens.space[1],
        minHeight: 220,
        overflowY: 'auto',
      }}
    >
      <Text as="h3" variant="caption" style={{ margin: 0, color: tokens.color.ink.secondary }}>
        3D object tree
      </Text>
      {sortedNodes.length === 0 ? (
        <Text variant="caption" as="p" style={{ margin: 0 }}>
          No BIM or 3D objects are loaded.
        </Text>
      ) : null}
      {sortedNodes.map((node, index) => {
        const selected = selectedLayerId === node.layerId;
        return (
          <div
            key={node.id}
            role="treeitem"
            aria-selected={selected}
            style={{
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              padding: tokens.space[1],
              background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
            }}
          >
            <Stack direction="row" align="center" gap={1}>
              <button
                type="button"
                {...roving.getItemProps(index)}
                onClick={() => {
                  onSelectNode(node);
                  selectionBus.publish({
                    source: 'host',
                    ts: Date.now(),
                    features: [{ layerId: node.layerId, featureId: node.id }],
                  });
                }}
                style={{
                  appearance: 'none',
                  background: 'transparent',
                  border: 0,
                  color: 'inherit',
                  cursor: 'pointer',
                  textAlign: 'left',
                  flex: '1 1 auto',
                  minHeight: tokens.density.touchMin,
                  paddingInline: tokens.space[1],
                }}
                aria-label={`${node.label}; ${disciplineLabel(node)}`}
              >
                <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
                  <Icon name="account_tree" size={20} decorative />
                  <Text as="span" variant="body">
                    {node.label}
                  </Text>
                  <Chip label={disciplineLabel(node)} variant="neutral" />
                  {node.hasMissingGeometry ? (
                    <Chip icon="deployed_code_alert" label="Missing geometry" variant="warning" />
                  ) : null}
                </Stack>
              </button>
              <IconButton
                aria-label={node.visible ? `Hide ${node.label}` : `Show ${node.label}`}
                onClick={() => onToggleVisibility(node)}
              >
                <Icon name={node.visible ? 'visibility' : 'visibility_off'} size={20} decorative />
              </IconButton>
            </Stack>
          </div>
        );
      })}
    </section>
  );
}
